import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Sparkles, CalendarClock, Trash2, CheckCircle2, AlertCircle, Loader2, Clock, ExternalLink, Lightbulb, ImageOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  pending: 'bg-slate-100 text-slate-700',
  generating: 'bg-violet-100 text-violet-700',
  ready: 'bg-amber-100 text-amber-700',
  published: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

const STATUS_ICON = {
  pending: Clock,
  generating: Loader2,
  ready: Lightbulb,
  published: CheckCircle2,
  failed: AlertCircle,
};

const AIQueue = () => {
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const authHeader = { Authorization: `Bearer ${token}` };

  const [topics, setTopics] = useState('');
  const [keywords, setKeywords] = useState('');
  const [generateImage, setGenerateImage] = useState(true);
  const [startAt, setStartAt] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.toISOString().slice(0, 16);  // datetime-local format
  });
  const [intervalHours, setIntervalHours] = useState(24);
  const [submitting, setSubmitting] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [queue, setQueue] = useState({ items: [], counts: { pending: 0, generating: 0, ready: 0, published: 0, failed: 0 } });

  const loadQueue = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/ai-queue`, { headers: authHeader });
      setQueue(r.data);
    } catch (e) {
      console.error('Failed to load queue', e);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadQueue();
    // Auto-refresh every 15 seconds so admin sees status changes live
    const id = setInterval(loadQueue, 15000);
    return () => clearInterval(id);
  }, [loadQueue]);

  const addToQueue = async () => {
    const topicList = topics.split('\n').map(t => t.trim()).filter(Boolean);
    if (topicList.length === 0) {
      toast({ title: 'No topics', description: 'Enter at least one topic (one per line).', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const isoStart = new Date(startAt).toISOString();
      const r = await axios.post(
        `${API}/api/admin/ai-queue`,
        { topics: topicList, keywords, generate_image: generateImage, start_at: isoStart, interval_hours: intervalHours },
        { headers: authHeader }
      );
      toast({ title: 'Queued!', description: `${r.data.added} article(s) scheduled.` });
      setTopics('');
      await loadQueue();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Remove this item from the queue?')) return;
    try {
      await axios.delete(`${API}/api/admin/ai-queue/${id}`, { headers: authHeader });
      toast({ title: 'Removed' });
      await loadQueue();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const retryItem = async (id) => {
    try {
      await axios.post(`${API}/api/admin/ai-queue/${id}/retry`, {}, { headers: authHeader });
      toast({ title: 'Re-queued', description: 'The runner will retry within ~2 minutes.' });
      await loadQueue();
    } catch (e) {
      toast({ title: 'Retry failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const suggestTopics = async () => {
    setSuggesting(true);
    try {
      const r = await axios.post(`${API}/api/admin/ai-suggest-topics`, {}, { headers: authHeader });
      const fresh = r.data.topics || [];
      if (fresh.length === 0) {
        toast({ title: 'No topics returned', description: 'Try again or add posts first.', variant: 'destructive' });
        return;
      }
      // Append to existing textarea (don't blow away anything user typed)
      const existing = topics.trim();
      const joined = (existing ? existing + '\n' : '') + fresh.join('\n');
      setTopics(joined);
      toast({
        title: `${fresh.length} topics suggested`,
        description: `Based on your last ${r.data.based_on_count} published posts. Review & edit before queueing.`,
      });
    } catch (e) {
      toast({ title: 'Suggestion failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSuggesting(false);
    }
  };

  // Landmark keywords we surface to admin as a hint that AI-image will be skipped.
  const PAISLEY_LANDMARKS = [
    'paisley abbey', 'paisley museum', 'paisley town hall', 'thomas coats memorial',
    'coats memorial', 'russell institute', 'sma shot cottages', 'anchor mills',
    'paisley arts centre', 'paisley central library', 'paisley grammar school',
    'paisley canal', 'paisley cenotaph', 'stanely castle', 'thomas coats park',
    'fountain gardens', 'barshaw park', 'lagoon leisure centre', 'paisley war memorial',
    'oakshaw trinity church', 'st mirin', 'coats observatory', 'paisley pirates',
    'paisley town centre', 'high street paisley', 'paisley cross', 'ferguslie',
    'the bungalow', 'paisley harbour', 'paisley piazza', 'paisley railway station',
    'paisley gilmour street', 'paisley canal station', 'love street', 'st mirren',
    'kelburne cinema', 'glen cinema', 'the swan paisley',
  ];
  const detectedLandmarks = topics
    .split('\n')
    .map(t => t.trim())
    .filter(Boolean)
    .map(t => ({
      topic: t,
      landmark: PAISLEY_LANDMARKS.find(kw => t.toLowerCase().includes(kw)),
    }))
    .filter(x => x.landmark);

  const fmt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard">
                <Button variant="outline" size="sm" data-testid="back-to-dashboard">
                  <ArrowLeft size={16} className="mr-2" /> Dashboard
                </Button>
              </Link>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Sparkles size={20} className="text-violet-500" />
                AI Publishing Queue
              </h1>
            </div>
            <p className="text-xs text-gray-500 hidden md:block">Auto-refreshes every 15s</p>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[420px_1fr] gap-8">
        {/* Add to queue */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 self-start" data-testid="ai-queue-form">
          <h2 className="text-lg font-bold text-slate-900 mb-1">Schedule new articles</h2>
          <p className="text-xs text-gray-500 mb-5">
            Paste one topic per line. Each topic becomes a 1200–1500 word article spaced by the interval below.
            Generated articles land in <span className="font-semibold text-amber-700">"Ready to review"</span> — you click <strong>Edit &amp; publish</strong> to review and publish them yourself.
          </p>

          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="topics" className="text-slate-900 font-medium">Topics (one per line) *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={suggestTopics}
                  disabled={suggesting}
                  className="h-7 px-2.5 text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
                  data-testid="suggest-topics-btn"
                >
                  {suggesting ? (
                    <><Loader2 size={11} className="mr-1.5 animate-spin" /> Thinking…</>
                  ) : (
                    <><Lightbulb size={11} className="mr-1.5" /> Suggest 10 topics</>
                  )}
                </Button>
              </div>
              <Textarea
                id="topics"
                value={topics}
                onChange={(e) => setTopics(e.target.value)}
                placeholder={"Best coffee shops in Paisley town centre\nPaisley Halloween events 2026\nTop 5 walks around Renfrewshire\nGuide to Paisley Abbey for tourists\nWeekend day trips from Paisley"}
                className="min-h-[160px] font-mono text-xs"
                data-testid="topics-textarea"
              />
              <p className="text-xs text-gray-400 mt-1.5">
                {topics.split('\n').map(t => t.trim()).filter(Boolean).length} topic(s)
              </p>
            </div>

            <div>
              <Label htmlFor="kw" className="text-slate-900 font-medium">Shared keywords (optional)</Label>
              <Input
                id="kw"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="e.g. Paisley, Renfrewshire, local guide"
                className="mt-2"
                data-testid="keywords-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="start" className="text-slate-900 font-medium">Start publishing at</Label>
                <Input
                  id="start"
                  type="datetime-local"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  className="mt-2"
                  data-testid="start-input"
                />
              </div>
              <div>
                <Label htmlFor="iv" className="text-slate-900 font-medium">Interval (hours)</Label>
                <Input
                  id="iv"
                  type="number"
                  min="1"
                  max="168"
                  value={intervalHours}
                  onChange={(e) => setIntervalHours(parseInt(e.target.value, 10) || 24)}
                  className="mt-2"
                  data-testid="interval-input"
                />
                <p className="text-xs text-gray-400 mt-1">24 = daily</p>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none pt-1">
              <input
                type="checkbox"
                checked={generateImage}
                onChange={(e) => setGenerateImage(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-violet-500 focus:ring-violet-500"
                data-testid="image-checkbox"
              />
              <span className="text-sm text-slate-700">Generate featured image for each (Nano Banana)</span>
            </label>

            {/* Real-image policy notice — surfaces whenever the topic list contains a Paisley landmark */}
            {generateImage && detectedLandmarks.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs" data-testid="landmark-notice">
                <div className="flex items-start gap-2">
                  <ImageOff size={14} className="text-amber-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-900 mb-1">Real-photo policy: AI image will be skipped</p>
                    <p className="text-amber-800 leading-relaxed">
                      We never AI-generate images of real Paisley landmarks (an AI render of Paisley Abbey would be inaccurate
                      and damage credibility). For these articles, upload an authentic photo in the editor after publishing.
                    </p>
                    <ul className="mt-2 space-y-0.5 text-amber-900">
                      {detectedLandmarks.slice(0, 5).map((d, i) => (
                        <li key={i} className="truncate" data-testid={`landmark-row-${i}`}>
                          &middot; <span className="font-medium">{d.topic}</span>
                          <span className="text-amber-600 italic"> (matched: {d.landmark})</span>
                        </li>
                      ))}
                      {detectedLandmarks.length > 5 && (
                        <li className="text-amber-600 italic">+ {detectedLandmarks.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={addToQueue}
              disabled={submitting || !topics.trim()}
              className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 mt-2"
              data-testid="add-to-queue-btn"
            >
              {submitting ? <><Loader2 size={14} className="mr-2 animate-spin" /> Adding…</> : <><CalendarClock size={14} className="mr-2" /> Add to queue</>}
            </Button>
          </div>
        </div>

        {/* Queue list */}
        <div>
          {/* Stat tiles */}
          <div className="grid grid-cols-5 gap-3 mb-5">
            {[
              { key: 'pending', label: 'Pending', color: 'text-slate-900' },
              { key: 'generating', label: 'Generating', color: 'text-violet-600' },
              { key: 'ready', label: 'Ready to review', color: 'text-amber-600' },
              { key: 'published', label: 'Published', color: 'text-green-600' },
              { key: 'failed', label: 'Failed', color: 'text-red-600' },
            ].map(t => (
              <div key={t.key} className="bg-white rounded-lg p-3 border border-gray-100" data-testid={`stat-${t.key}`}>
                <div className={`text-2xl font-bold ${t.color}`}>{queue.counts[t.key]}</div>
                <div className="text-xs text-gray-500 mt-0.5">{t.label}</div>
              </div>
            ))}
          </div>

          {/* Queue table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Queue ({queue.items.length})</h3>
            </div>
            {queue.items.length === 0 ? (
              <div className="p-12 text-center text-gray-500 text-sm" data-testid="empty-queue">
                <Sparkles size={32} className="mx-auto mb-3 text-gray-300" />
                Queue is empty. Add topics on the left to schedule auto-published articles.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="queue-table">
                <thead className="bg-slate-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Topic</th>
                    <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">Scheduled for</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-right px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {queue.items.map(item => {
                    const Icon = STATUS_ICON[item.status] || Clock;
                    return (
                      <tr key={item.id} className="hover:bg-slate-50/60" data-testid={`queue-row-${item.id}`}>
                        <td className="px-4 py-3">
                          <div className="text-slate-900 font-medium truncate max-w-md">{item.topic}</div>
                          {item.keywords && <div className="text-xs text-gray-400 mt-0.5 truncate">kw: {item.keywords}</div>}
                          {item.error && <div className="text-xs text-red-500 mt-0.5 truncate" data-testid={`err-${item.id}`}>{item.error}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 hidden sm:table-cell whitespace-nowrap">{fmt(item.publish_at)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${STATUS_STYLES[item.status] || ''}`}>
                            <Icon size={11} className={item.status === 'generating' ? 'animate-spin' : ''} />
                            {item.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {item.status === 'ready' && item.post_id && (
                            <Link
                              to={`/admin/posts/edit/${item.post_id}`}
                              className="text-xs font-semibold text-amber-700 hover:text-amber-900 inline-flex items-center gap-1 mr-3 px-2 py-1 rounded bg-amber-50 hover:bg-amber-100"
                              data-testid={`edit-${item.id}`}
                            >
                              Edit & publish
                            </Link>
                          )}
                          {item.status === 'published' && item.post_slug && (
                            <a
                              href={`/news/${item.post_slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-cyan-600 hover:text-cyan-800 inline-flex items-center gap-1 mr-3"
                              data-testid={`view-${item.id}`}
                            >
                              View <ExternalLink size={11} />
                            </a>
                          )}
                          {item.status === 'failed' && (
                            <button
                              onClick={() => retryItem(item.id)}
                              className="text-xs font-semibold text-red-700 hover:text-red-900 inline-flex items-center gap-1 mr-3 px-2 py-1 rounded bg-red-50 hover:bg-red-100"
                              data-testid={`retry-${item.id}`}
                            >
                              Retry
                            </button>
                          )}
                          {item.status !== 'generating' && (
                            <button
                              onClick={() => deleteItem(item.id)}
                              className="text-xs text-gray-400 hover:text-red-600 inline-flex items-center gap-1"
                              data-testid={`del-${item.id}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIQueue;
